'use client';
import { IMessage, IMessagesQueryParams } from '@ui/components/organisms/message';
import { PostItemAuthor } from './post-item-author';
import { ContentType, ContextUserType } from '@ui/constants/context';
import { useMessageContext } from '@ui/components/organisms/message';
import { MessageItemDescription } from '@ui/components/organisms/message/components/message-list/message-item-description';
import { PostItemsMoreOptions } from './post-item-more-option';
import { MessageItemFiles } from '@ui/components/organisms/message/components/message-list/message-item-files';
import Link from 'next/link';
import React, { ReactNode, useRef, forwardRef, useId, useCallback, useState, useMemo } from 'react';
import { PostItemReactions } from './post-item-reactions';
import { twMerge } from 'tailwind-merge';
import { useMessageUpdateFormContext } from '@ui/contexts/message-update-form-context';
import PostItemAnalyticsModal from './post-item-analytics';
import { ItemUpdateMessageContentWrapper } from '../common/item-update-message-content-wrapper';
import { Icon, MessageBlocked } from '@ui/components';
import { PinIcon } from 'lucide-react';
import { DynamicRouteBuilder } from '@ui/constants';

interface Props {
  post: IMessage;
  userType: ContextUserType;
  userId: string;
  creator_id: string;
  communityPostRoute: string;
  isOwnPage?: boolean;
  isLastPost?: boolean;
  query: IMessagesQueryParams;
  contentType: ContentType;
  isDynamicRoute?: boolean;
  communityDetails: NonNullable<IMessage['community_details']>;
  moderators_ids: string[];
}

interface ConditionalWrapperProps {
  condition: boolean;
  wrapper: (children: ReactNode) => JSX.Element;
  children: ReactNode;
  id: string;
}

const ConditionalPostLinkWrapper = forwardRef<HTMLDivElement, ConditionalWrapperProps>(
  ({ condition, wrapper, children }, ref) =>
    condition ? <div ref={ref}>{wrapper(children)}</div> : <div ref={ref}>{children}</div>,
);

ConditionalPostLinkWrapper.displayName = 'ConditionalPostLinkWrapper';

const PostItem = ({
  post,
  userType,
  userId,
  communityPostRoute,
  isOwnPage,
  isLastPost,
  query,
  creator_id,
  contentType,
  isDynamicRoute,
  communityDetails,
  moderators_ids,
}: Props) => {
  const id = useId();
  const updateRef = useRef<HTMLDivElement>(null);
  const { setTargetMessage } = useMessageUpdateFormContext();
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const messageState = useMessageContext('creator');

  const onUpdateTrigger = useCallback(() => {
    if (updateRef.current) {
      setTargetMessage({
        ...post,
        isPost: true,
        targetRef: updateRef.current,
        type: 'post',
        query,
      });
    }
  }, [post, setTargetMessage]);

  const onOpenPostAnalytics = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      messageState.onGetPostAnalytics(post?.id);
      setTimeout(() => {
        setIsModalOpen(true);
      }, 0);
    },
    [post?.id, messageState],
  );

  const postLink = useMemo(() => {
    if (isDynamicRoute) {
      // Use post.conversation_id if available, otherwise fallback to communityDetails.conversation_id or query.conversation_id
      const conversationId =
        post.conversation_id || communityDetails?.conversation_id || query?.conversation_id;
      const route = DynamicRouteBuilder.postBySlug(
        communityDetails.creator_display_name || communityDetails.creator_id,
        conversationId,
        post.slug || post.id,
      );
      // return `/creators/${communityDetails.creator_display_name || communityDetails.creator_id}/communities/${conversationId}/posts/${post.slug || post.id}`;
      return route;
    }
    // Handle bad post route
    if (communityPostRoute.includes('undefined')) {
      if (communityDetails?.conversation_type === 'community') {
        // replace the first undefined with the community id
        // http://localhost:3001/creators/KOR_Crew/communities/undefined/posts/undefined/?slug=be-part-of-building-kor-crew-zkd1ac
        // replace the second undefined with the post id
        // http://localhost:3001/creators/KOR_Crew/communities/undefined/posts/undefined/?slug=be-part-of-building-kor-crew-zkd1ac
        return communityPostRoute
          .replace('undefined', communityDetails.conversation_id)
          .replace('undefined', post.id);
      }
      console.warn('[BAD_COMMUNITY_URL_CAUGHT]', communityPostRoute);
    }
    return `${communityPostRoute}?slug=${post?.slug || post?.id}`;
  }, [
    communityPostRoute,
    post?.slug,
    post?.id,
    post?.conversation_id,
    communityDetails?.conversation_id,
    communityDetails?.creator_display_name,
    communityDetails?.creator_id,
    communityDetails?.conversation_type,
    query?.conversation_id,
    isDynamicRoute,
    userType,
  ]);

  const enableContentFlagging = useMemo(() => {
    if (post.user_id === userId) return false;
    if (userType === 'creator') return false;
    if (communityDetails.creator_id === userId) return false;

    return true;
  }, [post.creator_id, communityDetails.creator_id, post.user_id, userId]);

  return (
    <ItemUpdateMessageContentWrapper ref={updateRef}>
      <div className={twMerge('flex flex-col', !isOwnPage && 'px-4 py-2 border rounded')}>
        <ConditionalPostLinkWrapper
          id={id}
          condition={!isOwnPage}
          wrapper={(children) => (
            <Link
              href={postLink}
              className={twMerge('block space-y-4 bg-white', isLastPost && 'border-b-0')}>
              {children}
            </Link>
          )}>
          <div className={twMerge('space-y-4', 'flex flex-col justify-between')}>
            <div className="flex justify-between">
              <PostItemAuthor
                post={post}
                postOwnerUserType={post.user_type === 2 ? 'creator' : 'fan'}
                userType={userType}
                creatorId={creator_id}
                isDynamicRoute={isDynamicRoute}
                communityDetails={communityDetails}
                moderators_ids={moderators_ids}
                isInsideLink={!isOwnPage}
              />
              <div className="flex items-center">
                {post.pinned_at && (
                  <PinIcon className="text-primaryActive font-thin h-4 w-4 fill-white rotate-12" />
                )}
                <div className="flex items-center">
                  {userType === 'creator' && !post.draft && (
                    <>
                      {post?.user_id === creator_id && (
                        <div
                          className="p-2 cursor-pointer rounded hover:bg-gray-200"
                          onClick={onOpenPostAnalytics}>
                          <Icon type="slimGraph" />
                        </div>
                      )}
                    </>
                  )}
                  <PostItemsMoreOptions
                    post={post}
                    userType={userType}
                    userId={userId}
                    enableContentFlagging={enableContentFlagging}
                    onUpdateTrigger={onUpdateTrigger}
                    dropdownOpen={dropDownOpen}
                    setDropdownOpen={setDropDownOpen}
                    contentType={contentType}
                    communityDetails={communityDetails}
                    moderators_ids={moderators_ids}
                    reportLabel="Report post"
                  />
                </div>
              </div>
            </div>
            {!post.block && post.name && (
              <p className="font-nunito text-md font-bold" style={{ overflowWrap: 'break-word' }}>
                {post.name}
              </p>
            )}

            {post.block && <MessageBlocked userType={userType} />}

            {!post.block && (
              <MessageItemDescription
                truncate
                message={post}
                isOwnPage={isOwnPage}
                className="max-w-full rounded-none bg-transparent p-0"
              />
            )}

            {!post.block && post?.files && post?.files?.images?.length > 0 && (
              <MessageItemFiles className="max-w-full" files={post.files} />
            )}
          </div>

          {!post.draft && (
            <div className="pt-4">
              <PostItemReactions
                post={post}
                userType={userType}
                hierarchyType="post"
                onTriggerReplyForm={() => {}}
                enableReply={contentType === 'post'}
              />
            </div>
          )}
        </ConditionalPostLinkWrapper>

        {isModalOpen && (
          <PostItemAnalyticsModal
            isOpen={isModalOpen}
            isLoading={messageState?.isAnalyticsLoading}
            data={messageState?.analyticsData}
            postName={post.name}
            onClose={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsModalOpen(false);
            }}
          />
        )}
      </div>
    </ItemUpdateMessageContentWrapper>
  );
};

export default React.memo(PostItem);
